import React from 'react';
import styled, { css } from 'styled-components';
import { ArrowDown8, IconComponent } from '@honeybook/icons';
import { fontSizes } from '../../styles/fonts';
import { setIconFillColor } from '../../styles/mixins';
import spacing from '../../styles/spacing';
import { cornerRadiusRound } from '../../styles/variables';
import { transition } from '../../styles/animations';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { vars } from '../../theme';
import { applyOpacity } from '../../utils/color.utils';

export type HBStatusTheme =
    | 'neutral'
    | 'accent'
    | 'success'
    | 'warning'
    | 'danger';

/** 'primary' renders a filled badge, 'secondary' shows a dot + label on transparent background, 'dot' renders only a colored dot. */
export type HBStatusType = 'primary' | 'secondary' | 'dot';

export type HBStatusSize = 'sm' | 'xs';

interface HBStatusPrimary {
    /** Display mode. 'primary' renders a filled pill; 'secondary' renders a transparent dot-and-label pair; 'dot' renders only the colored dot. */
    type: 'primary';
    /** Text label displayed next to the leading dot or icon. Required for primary and secondary types. */
    label: string;
    /** Appends a down-arrow icon and auto-enables clickable behavior. */
    dropdown?: boolean;
    /**
     * Optional. When set, replaces the theme dot in the leading slot with any `IconComponent`.
     * Prefer **12×12** icons (e.g. `ArrowLineUpRight12`). Use sparingly.
     * Not supported on `secondary` or `dot` — those always show the dot.
     */
    prefix?: IconComponent;
}

interface HBStatusSecondary {
    /** Display mode. 'primary' renders a filled pill; 'secondary' renders a transparent dot-and-label pair; 'dot' renders only the colored dot. */
    type: 'secondary';
    /** Text label displayed next to the leading dot. Required for primary and secondary types. */
    label: string;
    dropdown?: boolean;
}

interface HBStatusDot {
    /** Display mode. 'primary' renders a filled pill; 'secondary' renders a transparent dot-and-label pair; 'dot' renders only the colored dot. */
    type: 'dot';
    label?: never;
    dropdown?: never;
}

export interface HBStatusBaseProps {
    /** CSS class forwarded to the root wrapper element. */
    className?: string;
    /** Semantic color theme controlling background, text, dot, and icon colors. */
    theme?: HBStatusTheme;
    /** Badge height. 'sm' is the standard size (24px); 'xs' is compact (16px). Defaults to 'sm'. */
    size?: HBStatusSize;
    /** Adds hover styling and cursor-pointer. Set automatically when `dropdown` is true. */
    clickable?: boolean;
    /** CSS max-width for the label text; defaults to '100px', text overflows with ellipsis. */
    maxTextWidth?: string;
}

export type HBStatusProps = HBStatusBaseProps &
    (HBStatusPrimary | HBStatusSecondary | HBStatusDot) &
    MustIncludeOneOfBasicTestIdProps;

const HBStatus = (props: HBStatusProps) => {
    const {
        className,
        theme = 'neutral',
        type,
        size = 'sm',
        label,
        clickable = false,
        dropdown = false,
        maxTextWidth = '100px',
        testId,
        testIdName
    } = props;

    const prefix = props.type === 'primary' ? props.prefix : undefined;
    const PrefixComponent = prefix;

    const SuffixIcon = dropdown ? ArrowDown8 : undefined;

    const _clickable = clickable || dropdown;

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'status',
        componentName: 'HBStatus'
    });

    const leadingTestId = finalTestId ? `${finalTestId}-leading` : undefined;

    // Primary: dot by default, or optional `prefix` icon. Secondary and dot: always the theme `Dot`.
    const leading =
        PrefixComponent != null ? (
            <LeadingIconWrapper data-testid={leadingTestId} aria-hidden>
                <PrefixComponent color={THEME[theme].dotColor} />
            </LeadingIconWrapper>
        ) : (
            <Dot
                theme={theme}
                size={size}
                aria-hidden
                data-testid={leadingTestId}
            />
        );

    return (
        <HBStatusWrapper
            theme={theme}
            type={type}
            size={size}
            clickable={_clickable}
            data-testid={finalTestId}
            className={className}
        >
            {type === 'dot' ? (
                leading
            ) : (
                <>
                    {leading}
                    <TextWrapper
                        size={size}
                        type={type}
                        clickable={_clickable}
                        title={label}
                        maxTextWidth={maxTextWidth}
                        $withPrefix={PrefixComponent != null}
                    >
                        {label}
                    </TextWrapper>
                    {SuffixIcon && (
                        <SuffixIconWrapper>
                            <SuffixIcon />
                        </SuffixIconWrapper>
                    )}
                </>
            )}
        </HBStatusWrapper>
    );
};

export default HBStatus;

const STRUCTURE: Record<
    HBStatusSize,
    {
        lineHeight: string;
        height: string;
        padding: string;
        fontSize: string;
        dotSize: string;
    }
> = {
    xs: {
        lineHeight: `${spacing[16]}`,
        height: `${spacing[16]}`,
        padding: `0 ${spacing[6]}`,
        fontSize: fontSizes.s10,
        dotSize: `${spacing[4]}`
    },
    sm: {
        lineHeight: `${spacing[24]}`,
        height: `${spacing[24]}`,
        padding: `0 ${spacing[8]}`,
        fontSize: fontSizes.s14,
        dotSize: `${spacing[6]}`
    }
};

const THEME: Record<
    HBStatusTheme,
    {
        backgroundColor: string;
        textColor: string;
        dotColor: string;
        iconColor?: string;
    }
> = {
    neutral: {
        backgroundColor: vars.colors.surface.neutral,
        textColor: vars.colors.text.secondary,
        dotColor: vars.colors.base.neutral,
        iconColor: vars.colors.text.secondary
    },
    accent: {
        backgroundColor: vars.colors.surface.accent,
        textColor: vars.colors.text.accent,
        dotColor: vars.colors.base.accent,
        iconColor: vars.colors.text.accent
    },
    success: {
        backgroundColor: vars.colors.surface.success,
        textColor: vars.colors.text.success,
        dotColor: vars.colors.base.success,
        iconColor: vars.colors.text.success
    },
    warning: {
        backgroundColor: vars.colors.surface.warning,
        textColor: vars.colors.text.warning,
        dotColor: vars.colors.base.warning,
        iconColor: vars.colors.text.warning
    },
    danger: {
        backgroundColor: vars.colors.surface.danger,
        textColor: vars.colors.text.danger,
        dotColor: vars.colors.base.danger,
        iconColor: vars.colors.text.danger
    }
};

const HBStatusWrapper = styled.div<
    Pick<HBStatusProps, 'theme' | 'type' | 'size' | 'clickable'>
>`
    display: inline-flex;
    gap: ${spacing[4]};
    align-items: center;
    justify-content: center;
    height: ${props => STRUCTURE[props.size || 'sm'].height};
    padding: ${props =>
        props.type === 'dot' ? '0' : STRUCTURE[props.size || 'sm'].padding};
    ${props =>
        (props.type === 'primary' || props.type === 'secondary') &&
        `border-radius: ${cornerRadiusRound};`}
    background-color: ${props =>
        props.type === 'primary'
            ? THEME[props.theme || 'neutral'].backgroundColor
            : 'transparent'};
    font-family: ${vars.fonts.body};
    color: ${props => THEME[props.theme || 'neutral'].textColor};
    position: relative;
    overflow: hidden;
    cursor: ${props => (props.clickable ? 'pointer' : 'default')};
    width: fit-content;

    ${props => setIconFillColor(THEME[props.theme || 'neutral'].iconColor)}
    ${transition('background-color')}

    &:hover {
        ${props =>
            props.clickable &&
            props.type === 'primary' &&
            `
            &::after {
                content: '';
                position: absolute;
                inset: 0;
                background-color: ${applyOpacity(vars.colors.text.primary, 10)};
                pointer-events: none;
            }
        `}

        ${props =>
            props.clickable &&
            props.type === 'secondary' &&
            `
            background-color: ${applyOpacity(vars.colors.text.primary, 10)}
        `}
    }
    &:focus-visible {
        outline: 3px solid ${vars.colors.stroke.keyboardFocus};
        border-radius: ${cornerRadiusRound};
        offset: 1px;
    }
`;

const TextWrapper = styled.span<
    Pick<HBStatusProps, 'size' | 'type' | 'clickable' | 'maxTextWidth'> & {
        /** When `prefix` is set, skip baseline nudge so text aligns with the leading icon. */
        $withPrefix?: boolean;
    }
>`
    max-width: ${props => props.maxTextWidth};
    font-size: ${props => STRUCTURE[props.size || 'sm'].fontSize};
    color: ${props =>
        props.type === 'secondary' ? vars.colors.text.primary : 'inherit'};
    position: relative;
    bottom: ${props => (props.$withPrefix ? 0 : '-0.03em')};
    line-height: ${props => STRUCTURE[props.size || 'sm'].lineHeight};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    ${props =>
        props.type === 'secondary' &&
        props.clickable &&
        css`
            text-decoration: underline;
            text-underline-offset: 3px;
            text-decoration-color: ${vars.colors.text.tertiary};
            ${HBStatusWrapper}:hover & {
                text-decoration-color: ${vars.colors.text.secondary};
            }
        `}
`;

/** 12×12 slot; line-height 0 removes inline strut so the icon centers with label text. */
const LeadingIconWrapper = styled.div`
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 12px;
    height: 12px;
    line-height: 0;

    & > i {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        vertical-align: middle;
    }

    & svg {
        display: block;
    }
`;

const SuffixIconWrapper = styled.div`
    margin-left: ${spacing[2]};
`;

const Dot = styled.div<Pick<HBStatusProps, 'theme' | 'size'>>`
    width: ${props => STRUCTURE[props.size].dotSize};
    height: ${props => STRUCTURE[props.size].dotSize};
    border-radius: ${cornerRadiusRound};
    background-color: ${props => THEME[props.theme].dotColor};
`;
